import React, { PropsWithChildren, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';
import theme from '|/components/base/theme';
import Close from '|/icons/close.svg';
import { ModalFrameworkContextProvider } from '../../../helpers/app-framework';

const defaultProps = {
  title: null as null | string,
  showHeaderClose: false,
  allowBackdropClick: false,
  onClose: () => {},
  mobileFullscreen: false
};

const overlayDefaultProps = {
  allowBackdropClick: false,
  onClose: () => {}
};

const OverlayStyle = styled.div`
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  z-index: 1050;
  background: rgb(24 53 88 / 30%);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Overlay: React.FC<React.PropsWithChildren<
  typeof overlayDefaultProps
>> = ({ allowBackdropClick, onClose, children }) => {
  return (
    <OverlayStyle
      onClick={() => allowBackdropClick && onClose()}
      data-analytics-id="pp_modal_overlay"
    >
      {children}
    </OverlayStyle>
  );
};

const defaultModalHeaderProps = {
  title: '',
  showHeaderClose: false,
  onClose: () => {}
};

const ModalHeaderStyle = styled.div`
  background: var(--colors-neutral-white);
  position: sticky;
  text-align: center;
  top: 0;
  padding: 15px 0;
  box-shadow: 0 2px 4px -1px rgb(25 41 61 / 18%);
  z-index: 300;

  h1 {
    ${theme.font.header.xs};
    color: var(--colors-primary-blue-6);

    @media (min-width: ${theme.breakpoints.sm}) {
      ${theme.font.header.s};
    }
  }

  svg {
    width: 14px;
    height: 14px;
    position: absolute;
    top: 17px;
    bottom: 0;
    right: 15px;
    cursor: pointer;
    line-height: 20px;
  }
`;

const ModalHeader = ({
  title,
  showHeaderClose,
  onClose
} = defaultModalHeaderProps) => {
  return (
    <ModalHeaderStyle>
      <h1>{title}</h1>
      {showHeaderClose && (
        <Close onClick={onClose} data-analytics-id="pp_modal_header_close" />
      )}
    </ModalHeaderStyle>
  );
};

const defaultModalWrapperProps = {
  title: null as null | string,
  showHeaderClose: false,
  onClose: () => {},
  mobileFullscreen: false
};

type ModalContentStyleProps = Pick<
  typeof defaultModalWrapperProps,
  'mobileFullscreen'
>;

const ModalContentStyle = styled.section<ModalContentStyleProps>`
  max-width: 704px;
  width: 100%;
  overflow-y: auto;
  background-color: var(--colors-neutral-gray-1);

  @media (min-width: ${theme.breakpoints.sm}) {
    max-height: 95vh;
  }

  ${props =>
    props.mobileFullscreen &&
    css`
      @media (max-width: ${theme.breakpoints.sm}) {
        width: 100vw;
        height: 100dvh;
        position: fixed;
        inset: 0;
        top: 100%;
        transition: top 0.15s ease-out;
        padding-bottom: env(safe-area-inset-bottom);

        &.in {
          top: 0;
        }
      }
    `}
`;

const defaultSlideProps = {
  setIsReadyToSlide: (_: boolean) => {}
};

const StartSlideOnMount = ({ setIsReadyToSlide } = defaultSlideProps) => {
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsReadyToSlide(true);
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [setIsReadyToSlide]);
  return null;
};

const ModalWrapper: React.FC<React.PropsWithChildren<
  typeof defaultModalWrapperProps
>> = ({
  title,
  showHeaderClose,
  children,
  onClose,
  mobileFullscreen
} = defaultModalWrapperProps) => {
  const [isReadyToSlide, setIsReadyToSlide] = useState(false);
  return (
    <>
      <ModalContentStyle
        mobileFullscreen={mobileFullscreen}
        onClick={e => e.stopPropagation()}
        className={isReadyToSlide ? 'in' : ''}
        data-analytics-id="pp_modal_header"
      >
        {(title || showHeaderClose) && (
          <ModalHeader
            title={title || ''}
            showHeaderClose={showHeaderClose}
            onClose={onClose}
          />
        )}
        {children}
      </ModalContentStyle>
      <StartSlideOnMount setIsReadyToSlide={setIsReadyToSlide} />
    </>
  );
};

const Modal: React.FC<PropsWithChildren<typeof defaultProps>> = ({
  children,
  title,
  showHeaderClose,
  allowBackdropClick,
  mobileFullscreen,
  onClose
} = defaultProps) => {
  useEffect(() => {
    document.body.className = `${document.body.className} modal-scroll-lock`;
    return () => {
      document.body.className = document.body.className.replace(
        'modal-scroll-lock',
        ''
      );
    };
  }, []);
  useEffect(() => {
    document.body.style.top = `-${window.scrollY}px`;
    return () => {
      const scrollY = document.body.style.top;
      document.body.style.top = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    };
  }, []);

  return createPortal(
    <Overlay allowBackdropClick={allowBackdropClick} onClose={onClose}>
      <ModalWrapper
        title={title}
        showHeaderClose={showHeaderClose}
        onClose={onClose}
        mobileFullscreen={mobileFullscreen}
      >
        <ModalFrameworkContextProvider>
          {children}
        </ModalFrameworkContextProvider>
      </ModalWrapper>
    </Overlay>,
    document.body
  );
};

export default Modal;
